/**
 * OSS 资源工具函数（统一处理字体、图片等资源）
 * 用于从 OSS 加载资源，如果失败则回退到本地资源
 */
/**
 * 获取 OSS 资源 URL（统一方法）
 * @param ossPath OSS 中的资源路径，例如 'font/Pin.ttf' 或 'image/background.jpg'
 * @returns 资源 URL（代理路径）
 */
export function getOssResourceUrl(ossPath) {
    const ossCdn = process.env.VUE_APP_OSS_CDN || '';
    if (ossCdn) {
        // 开发环境和生产环境都使用代理避免 CORS 问题
        // 开发环境：使用 webpack dev server 代理
        // 生产环境：使用 Express 服务器代理
        // 统一处理：移除路径开头的斜杠，确保路径格式一致
        const cleanPath = ossPath.startsWith('/') ? ossPath.slice(1) : ossPath;
        const proxyUrl = `/oss-resource/${cleanPath}`;
        return proxyUrl;
    }
    // 未配置 OSS，返回空字符串（由调用方处理本地资源）
    console.warn(`[OSS资源] 未配置 VUE_APP_OSS_CDN，无法使用 OSS 资源: ${ossPath}`);
    return '';
}
/**
 * 获取 OSS 图片 URL（兼容旧代码）
 * @deprecated 使用 getOssResourceUrl 代替
 */
export function getOssImageUrl(ossPath) {
    return getOssResourceUrl(ossPath);
}
/**
 * 预加载图片并测试是否可用
 * @param url 图片 URL
 * @param fallbackUrl 回退图片 URL
 * @returns Promise<string> 可用的图片 URL
 */
export function preloadImageWithFallback(url, fallbackUrl) {
    return new Promise((resolve) => {
        if (!url) {
            resolve(fallbackUrl || '');
            return;
        }
        const img = new Image();
        // 如果使用代理（/oss-resource），不需要设置 crossOrigin
        const useProxy = url.startsWith('/oss-resource');
        if (!useProxy) {
            img.crossOrigin = 'anonymous';
        }
        const timeout = setTimeout(() => {
            console.warn(`[图片加载] 超时（5秒）: ${url}`);
            if (fallbackUrl) {
                resolve(fallbackUrl);
            }
            else {
                resolve('');
            }
            img.onload = null;
            img.onerror = null;
        }, 5000);
        img.onload = () => {
            clearTimeout(timeout);
            resolve(url);
        };
        img.onerror = (error) => {
            clearTimeout(timeout);
            console.error(`[图片加载] 失败: ${url}`);
            // 输出详细的调试信息
            if (url.startsWith('/oss-resource/')) {
                const ossCdn = process.env.VUE_APP_OSS_CDN || '';
                if (ossCdn) {
                    const cleanPath = url.replace('/oss-resource/', '');
                    // 移除路径开头的斜杠（如果有）
                    const finalPath = cleanPath.startsWith('/') ? cleanPath.slice(1) : cleanPath;
                    const directUrl = `${ossCdn}/${finalPath}`;
                    console.error(`[图片加载] 调试信息:`);
                    console.error(`  代理 URL: ${url}`);
                    console.error(`  清理后的路径: ${cleanPath}`);
                    console.error(`  最终路径: ${finalPath}`);
                    console.error(`  直接 OSS URL: ${directUrl}`);
                    console.error(`  请检查:`);
                    console.error(`  1. 在浏览器中直接访问: ${directUrl}`);
                    console.error(`  2. 检查 PM2 日志中的代理转发路径`);
                    console.error(`  3. 确认 OSS 文件路径是否正确`);
                    console.error(`  4. 确认文件权限是否为公共读`);
                    console.error(`  5. 对比字体路径: /oss-resource/font/Pin.ttf (应该工作正常)`);
                }
            }
            resolve(fallbackUrl || '');
        };
        // 先设置错误处理，再设置 src，避免竞态条件
        img.src = url;
    });
}
/**
 * 测试 OSS 资源是否可访问（用于调试）
 * @param ossPath OSS 中的资源路径
 * @returns Promise<boolean> 是否可访问
 */
export async function testOssResource(ossPath) {
    const url = getOssResourceUrl(ossPath);
    if (!url) {
        console.warn(`[OSS测试] 未配置 OSS CDN，无法测试: ${ossPath}`);
        return false;
    }
    try {
        const response = await fetch(url, { method: 'HEAD' });
        const success = response.ok;
        console.log(`[OSS测试] ${ossPath}: ${success ? '✅ 可访问' : '❌ 不可访问'} (状态码: ${response.status})`);
        return success;
    }
    catch (error) {
        console.error(`[OSS测试] ${ossPath}: ❌ 请求失败`, error);
        return false;
    }
}
